import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDebounce } from '../../hooks';

const SCROLL_DELAY_MS = 200;
const DEBOUNCE_DELAY_MS = 300;

type UseScrollToElementOptions = {
    elementId: string;
    targetPath: string;
};

type UseScrollToElementReturn = {
    scrollToElement: () => void;
};

/**
 * Хук для навигации на страницу и прокрутки к элементу с заданным ID
 *
 * @param elementId - ID элемента, к которому нужно прокрутить
 * @param targetPath - Путь страницы, на которой находится элемент
 * @returns Функция для инициации навигации и прокрутки
 */
export const useScrollToElement = ({ elementId, targetPath }: UseScrollToElementOptions): UseScrollToElementReturn => {
    const navigate = useNavigate();
    const location = useLocation();
    const shouldScrollRef = useRef(false);
    const isNavigatingRef = useRef(false);

    useEffect(() => {
        if (!shouldScrollRef.current || location.pathname !== targetPath) {
            return;
        }

        const timeoutId = setTimeout(() => {
            const element = document.getElementById(elementId);

            if (element) {
                const offset = element.offsetTop - window.innerHeight / 2 + element.offsetHeight / 2;

                window.scrollTo({
                    top: offset,
                    behavior: 'smooth',
                });
            }

            shouldScrollRef.current = false;
        }, SCROLL_DELAY_MS);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [location.pathname, elementId, targetPath]);

    const handleScrollToElement = useCallback(() => {
        if (location.pathname === targetPath) {
            const element = document.getElementById(elementId);

            if (element) {
                const offset = element.offsetTop - window.innerHeight / 2 + element.offsetHeight / 2;

                window.scrollTo({
                    top: offset,
                    behavior: 'smooth',
                });
            }
        } else if (!isNavigatingRef.current) {
            // Prevent multiple navigations
            isNavigatingRef.current = true;
            shouldScrollRef.current = true;
            navigate(targetPath);

            // Reset navigation flag after navigation completes
            setTimeout(() => {
                isNavigatingRef.current = false;
            }, SCROLL_DELAY_MS + 100);
        }
    }, [location.pathname, targetPath, elementId, navigate]);

    // Use debounce hook to prevent multiple rapid clicks
    const scrollToElement = useDebounce(handleScrollToElement, DEBOUNCE_DELAY_MS);

    return { scrollToElement };
};
